import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const MatrixContainer = styled.section`
  min-height: 100vh;
  background: linear-gradient(135deg, #000000 0%, #0a0a0a 50%, #000000 100%);
  padding: 4rem 2rem;
  position: relative;
  overflow: hidden;
`;

const MatrixHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const MatrixTitle = styled(motion.h2)`
  font-family: 'Orbitron', monospace;
  font-size: clamp(2.5rem, 6vw, 5rem);
  font-weight: 900;
  color: #00ffff;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  margin-bottom: 1rem;
`;

const MatrixSubtitle = styled(motion.p)`
  font-family: 'Inter', sans-serif;
  font-size: clamp(1.1rem, 2.5vw, 1.8rem);
  color: #cccccc;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const ServiceCard = styled(motion.div)`
  background: rgba(0, 17, 34, 0.8);
  border: 1px solid rgba(0, 255, 255, 0.3);
  border-radius: 8px;
  padding: 2rem;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.1), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover::before {
    left: 100%;
  }
  
  &:hover {
    border-color: #00ffff;
    box-shadow: 0 0 30px rgba(0, 255, 255, 0.3);
    transform: translateY(-5px);
  }
`;

const ServiceIcon = styled.div`
  width: 60px;
  height: 60px;
  margin-bottom: 1.5rem;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 50%;
  border: 2px solid ${props => props.color || '#00ffff'};
`;

const ServiceTitle = styled.h3`
  font-family: 'Orbitron', monospace;
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const ServiceDescription = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: #cccccc;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ServiceFeature = styled.li`
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  color: #888888;
  margin-bottom: 0.5rem;
  padding-left: 1.5rem;
  position: relative;
  
  &::before {
    content: '▶';
    position: absolute;
    left: 0;
    color: #00ffff;
    font-size: 0.7rem;
  }
`;

const BackgroundGrid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(rgba(0, 255, 255, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 255, 255, 0.1) 1px, transparent 1px);
  background-size: 100px 100px;
  opacity: 0.2;
  pointer-events: none;
`;

const FloatingParticles = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
`;

const Particle = styled(motion.div)`
  position: absolute;
  width: 2px;
  height: 2px;
  background: #00ffff;
  border-radius: 50%;
`;

// SVG icon components
const OffensiveIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="16" cy="16" r="14" stroke="#ff0000" strokeWidth="2"/><circle cx="16" cy="16" r="6" stroke="#ff0000" strokeWidth="2"/><line x1="16" y1="2" x2="16" y2="8" stroke="#ff0000" strokeWidth="2"/><line x1="16" y1="24" x2="16" y2="30" stroke="#ff0000" strokeWidth="2"/><line x1="2" y1="16" x2="8" y2="16" stroke="#ff0000" strokeWidth="2"/><line x1="24" y1="16" x2="30" y2="16" stroke="#ff0000" strokeWidth="2"/></svg>
);
const DefensiveIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 4L28 8V16C28 23 16 28 16 28C16 28 4 23 4 16V8L16 4Z" stroke="#00ffff" strokeWidth="2" fill="rgba(0,255,255,0.08)"/></svg>
);
const AwarenessIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="6" y="8" width="20" height="16" rx="2" stroke="#ffff00" strokeWidth="2" fill="rgba(255,255,0,0.08)"/><line x1="10" y1="12" x2="22" y2="12" stroke="#ffff00" strokeWidth="2"/><line x1="10" y1="16" x2="22" y2="16" stroke="#ffff00" strokeWidth="2"/><line x1="10" y1="20" x2="18" y2="20" stroke="#ffff00" strokeWidth="2"/></svg>
);
const GRCIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="16" cy="16" r="14" stroke="#00ff00" strokeWidth="2"/><path d="M16 6V16L24 20" stroke="#00ff00" strokeWidth="2"/></svg>
);
const DFIRIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="6" y="8" width="20" height="14" rx="2" stroke="#ff6600" strokeWidth="2" fill="rgba(255,102,0,0.08)"/><rect x="10" y="12" width="12" height="6" rx="1" stroke="#ff6600" strokeWidth="2"/></svg>
);
const ManagedIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 28V4M16 4L10 10M16 4L22 10" stroke="#ff00ff" strokeWidth="2"/><circle cx="16" cy="20" r="6" stroke="#ff00ff" strokeWidth="2" fill="rgba(255,0,255,0.08)"/></svg>
);

const services = [
  {
    id: 1,
    title: 'Offensive Security',
    description: 'Proactive security testing to identify vulnerabilities before attackers exploit them.',
    features: [
      'Penetration Testing (Web, Mobile, Network, Wireless)',
      'Red Team Engagements',
      'Social Engineering (Phishing, Vishing, Physical)',
      'Secure Code Reviews (OWASP, CI/CD)'
    ],
    icon: <OffensiveIcon />,
    color: '#ff0000'
  },
  {
    id: 2,
    title: 'Defensive Security',
    description: 'Comprehensive protection and monitoring to defend against evolving threats.',
    features: [
      'Managed Vulnerability Assessments',
      'Threat Detection & Response Consulting',
      'SIEM Tuning & Log Analysis',
      'Cloud Security Hardening (Azure, AWS, GCP)'
    ],
    icon: <DefensiveIcon />,
    color: '#00ffff'
  },
  {
    id: 3,
    title: 'Cyber Awareness & Training',
    description: 'Transform your workforce into a human firewall through targeted security education.',
    features: [
      'Human Risk Management Programs',
      'Phishing Simulations & Social Engineering Drills',
      'Cybersecurity Policy Training (POPIA, ISO, GDPR)',
      'Executive & Board Cybersecurity Workshops'
    ],
    icon: <AwarenessIcon />,
    color: '#ffff00'
  },
  {
    id: 4,
    title: 'Governance, Risk & Compliance',
    description: 'Navigate complex regulatory requirements with expert guidance and strategic planning.',
    features: [
      'Gap Analysis & Risk Posture Reports (NIST, ISO, CIS)',
      'Compliance Readiness & Audit Support',
      'Cybersecurity Strategy & Roadmap Design',
      'Business Continuity & Incident Response Planning'
    ],
    icon: <GRCIcon />,
    color: '#00ff00'
  },
  {
    id: 5,
    title: 'Digital Forensics & Incident Response',
    description: 'Expert investigation and rapid response to security incidents and breaches.',
    features: [
      'Forensic Investigations (Disk, Cloud, Email, Network)',
      'Ransomware Triage & Containment',
      'Incident Response Playbook Creation',
      'Tabletop Exercises & Post-Incident Reviews'
    ],
    icon: <DFIRIcon />,
    color: '#ff6600'
  },
  {
    id: 6,
    title: 'Managed Security & Automation',
    description: 'Scalable security solutions with automated protection and continuous monitoring.',
    features: [
      'Automated Patch Management Systems',
      'Security-as-a-Service (CISO-as-a-Service, vSOC)',
      'Endpoint Detection & Response (EDR) Advisory',
      'Dark Web Monitoring & Breach Alerts'
    ],
    icon: <ManagedIcon />,
    color: '#ff00ff'
  }
];

const ServiceMatrix = () => {
  const [hoveredService, setHoveredService] = useState(null);

  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    delay: Math.random() * 5,
  }));

  return (
    <MatrixContainer>
      <BackgroundGrid />
      
      <FloatingParticles>
        {particles.map((particle) => (
          <Particle
            key={particle.id}
            style={{ left: `${particle.x}%`, top: `${particle.y}%` }}
            animate={{
              y: [0, -20, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              delay: particle.delay,
            }}
          />
        ))}
      </FloatingParticles>

      <MatrixHeader>
        <MatrixTitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
        >
          Service Matrix
        </MatrixTitle>
        
        <MatrixSubtitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          viewport={{ once: true }}
        >
          Advanced threat detection, proactive defense, and strategic cybersecurity solutions to protect your digital assets
        </MatrixSubtitle>
      </MatrixHeader>

      <ServiceGrid>
        {services.map((service, index) => (
          <ServiceCard
            key={service.id}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: index * 0.1 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.02 }}
            onHoverStart={() => setHoveredService(service.id)}
            onHoverEnd={() => setHoveredService(null)}
          >
            <ServiceIcon color={service.color}>
              {service.icon}
            </ServiceIcon>
            
            <ServiceTitle>{service.title}</ServiceTitle>
            
            <ServiceDescription>{service.description}</ServiceDescription>
            
            <ServiceFeatures>
              {service.features.map((feature, i) => (
                <ServiceFeature key={i}>{feature}</ServiceFeature>
              ))}
            </ServiceFeatures>
          </ServiceCard>
        ))}
      </ServiceGrid>
    </MatrixContainer>
  );
};

export default ServiceMatrix; 